<template>
  <v-table
    :count="count"
    :list="list"
    :is-loading="isLoading"
    :push-list="RouteConfig.cardChargeTypeYearlyAnalysis.push"
    :query-list="queryList"
    :is-show-store-select="true"
    :is-store-select-clearable="true"
    :is-show-search-input="true"
    search-placeholder="输入学员姓名、手机号或卡号"
    :is-show-month-range-picker="true"
    :is-show-xls-export-button="true"
    :excel-data="xlsData"
    excel-name="年卡使用分析"
    :isShowExportALl="true"
    :isExportingAll="isExporting"
    :exportAllText="exportAllText"
    :auto-scroll="false"
  >

    <template slot="header-item">
      <el-select
        class="table-filter-select"
        v-model="queryList.profit_state"
        clearable
        size="small"
        placeholder="价值状态"
        @change="handleFilterSelectChange('profit_state')"
      >
        <el-option
          v-for="(state, key) in yearlyCardProfitStates"
          :key="key"
          :label="state.name"
          :value="state.value"
        />
      </el-select>
      <el-select
        class="table-filter-select"
        v-model="queryList.used_scope"
        clearable
        size="small"
        placeholder="使用程度"
        @change="handleFilterSelectChange('used_scope')"
      >
        <el-option
          v-for="(scope, key) in yearlyCardUsedScopes"
          :key="key"
          :label="scope.name"
          :value="scope.value"
        />
      </el-select>
      <el-select
        class="table-filter-select"
        v-model="queryList.is_generic"
        size="small"
        placeholder="通用性"
        @change="handleFilterSelectChange('is_generic')"
      >
        <el-option
          v-for="(scope, key) in yearlyCardGenericScopes"
          :key="key"
          :label="scope.name"
          :value="scope.value"
        />
      </el-select>
    </template>

    <template slot="summary">
      <section class="yearly-analysis">
        <!-- 口径说明 -->
        <div class="yearly-analysis-tip hint-color">
          口径说明：价值差额 = 实付金额 − 使用次数 × 75 元（次卡等值），属机会成本估算而非现金盈亏。
          老师课时费为固定成本，学员多上一次课的边际成本接近 0；高频上课的学员同时也是课堂人气来源。
        </div>

        <!-- 总体数据 -->
        <div class="yearly-summary-list">
          <div class="yearly-summary-item">
            <div class="yearly-summary-value">{{ summary.total_cards }}</div>
            <div class="yearly-summary-label">已过期年卡总数（张）</div>
          </div>
          <div class="yearly-summary-item">
            <div class="yearly-summary-value">{{ summary.total_payment | toYuan }}</div>
            <div class="yearly-summary-label">总实付金额（元）</div>
            <div class="yearly-summary-tip">平均实付 {{ summary.avg_payment | toYuan }} 元/张</div>
          </div>
          <div class="yearly-summary-item">
            <div class="yearly-summary-value">{{ summary.total_used }}</div>
            <div class="yearly-summary-label">总使用次数（次）</div>
            <div class="yearly-summary-tip">人均 {{ summary.avg_used }} 次</div>
          </div>
          <div class="yearly-summary-item">
            <div class="yearly-summary-value" :class="totalProfitClass">
              {{ summary.total_profit | toYuan }}
            </div>
            <div class="yearly-summary-label">价值差额（次卡等值，元）</div>
            <div class="yearly-summary-tip" :class="totalProfitClass">
              整体价值利用率 {{ valueUsageRate }}%，平均单卡 {{ summary.avg_profit | toYuan }} 元
            </div>
          </div>
        </div>

        <!-- 价值状态结构（未用超=红 income-color、用超=绿 pay-color，与系统收支颜色惯例一致；点击卡片联动筛选列表） -->
        <div class="yearly-summary-list">
          <div
            class="yearly-summary-item yearly-summary-item-clickable"
            :class="{ 'yearly-summary-item-active': queryList.profit_state === yearlyCardProfitStates.profit.value }"
            @click="handleSummaryCardClick('profit_state', yearlyCardProfitStates.profit.value)"
          >
            <div class="yearly-summary-value income-color">{{ summary.profit_count }} 张</div>
            <div class="yearly-summary-label">未用超卡（结余 {{ summary.profit_amount | toYuan }} 元）</div>
            <div class="yearly-summary-tip income-color">
              使用未达保本线，可重点关注
            </div>
          </div>
          <div
            class="yearly-summary-item yearly-summary-item-clickable"
            :class="{ 'yearly-summary-item-active': queryList.profit_state === yearlyCardProfitStates.loss.value }"
            @click="handleSummaryCardClick('profit_state', yearlyCardProfitStates.loss.value)"
          >
            <div class="yearly-summary-value pay-color">{{ summary.loss_count }} 张</div>
            <div class="yearly-summary-label">用超卡（用超 {{ summary.loss_amount | toYuan }} 元）</div>
            <div class="yearly-summary-tip pay-color">高频上课是课堂人气来源，仅满员课程有机会成本</div>
          </div>
          <div
            class="yearly-summary-item yearly-summary-item-clickable"
            :class="{ 'yearly-summary-item-active': queryList.profit_state === yearlyCardProfitStates.breakeven.value }"
            @click="handleSummaryCardClick('profit_state', yearlyCardProfitStates.breakeven.value)"
          >
            <div class="yearly-summary-value">{{ summary.breakeven_count }} 张</div>
            <div class="yearly-summary-label">临界卡（±500 元）</div>
            <div class="yearly-summary-tip">用量与次卡等值相当</div>
          </div>
          <div
            class="yearly-summary-item yearly-summary-item-clickable"
            :class="{ 'yearly-summary-item-active': queryList.used_scope === yearlyCardUsedScopes.light.value }"
            @click="handleSummaryCardClick('used_scope', yearlyCardUsedScopes.light.value)"
          >
            <div class="yearly-summary-value income-color">{{ summary.light_used_count }} 张</div>
            <div class="yearly-summary-label">轻度使用卡（1~5 次）</div>
            <div class="yearly-summary-tip income-color">
              办了卡仅上过几节课，结余 {{ summary.light_used_profit | toYuan }} 元
            </div>
          </div>
          <div
            class="yearly-summary-item yearly-summary-item-clickable"
            :class="{ 'yearly-summary-item-active': queryList.used_scope === yearlyCardUsedScopes.heavy.value }"
            @click="handleSummaryCardClick('used_scope', yearlyCardUsedScopes.heavy.value)"
          >
            <div
              class="yearly-summary-value"
              :class="summary.heavy_used_profit >= 0 ? 'income-color' : 'pay-color'"
            >
              {{ summary.heavy_used_count }} 张
            </div>
            <div class="yearly-summary-label">重度使用卡（≥200 次）</div>
            <div
              class="yearly-summary-tip"
              :class="summary.heavy_used_profit >= 0 ? 'income-color' : 'pay-color'"
            >
              高频上课是课堂人气来源，合计价值差额 {{ summary.heavy_used_profit | toYuan }} 元
            </div>
          </div>
        </div>

        <!-- 两张分布图并排：使用次数分布 + 价值差额分布 -->
        <div class="yearly-chart-row">
          <!-- 使用次数分布 -->
          <div class="yearly-chart-box yearly-chart-half">
            <div class="yearly-chart-title">使用次数分布（平均保本线 {{ avgBreakevenUsed }} 次）</div>
            <div id="yearly-used-chart" class="yearly-chart"></div>
          </div>

          <!-- 价值差额分布 -->
          <div class="yearly-chart-box yearly-chart-half">
            <div class="yearly-chart-title">价值差额分布（实付金额 − 使用次数 × 75 元次卡等值）</div>
            <div id="yearly-profit-chart" class="yearly-chart"></div>
          </div>
        </div>

        <!-- 活动定价模拟器（独占一行：左侧参数面板，右侧敏感度曲线） -->
        <div class="yearly-chart-box">
          <div class="yearly-chart-title">活动定价模拟器（基于当前筛选结果推演，共 {{ usedNumbers.length }} 张年卡）</div>
          <div class="yearly-simulator">
            <div class="yearly-sim-main">
              <div class="yearly-sim-control">
                <div class="yearly-sim-label-row">
                  <div class="yearly-sim-label">活动价</div>
                  <div class="yearly-sim-input">
                    <v-input-money
                      v-model="simPrice"
                      size="small"
                      :is-show-suffix="true"
                      suffix-name="元"
                      placeholder="自定义"
                      @change="initSimChart"
                    />
                  </div>
                </div>
                <el-slider
                  v-model="simPrice"
                  :min="0"
                  :max="30000"
                  :step="100"
                  :marks="priceMarks"
                  @change="initSimChart"
                />
              </div>
              <div class="yearly-sim-control">
                <div class="yearly-sim-label-row">
                  <div class="yearly-sim-label">单次成本</div>
                  <div class="yearly-sim-input">
                    <v-input-money
                      v-model="simCost"
                      size="small"
                      :is-show-suffix="true"
                      suffix-name="元/次"
                      placeholder="自定义"
                      @change="initSimChart"
                    />
                  </div>
                </div>
                <el-slider
                  v-model="simCost"
                  :min="0"
                  :max="150"
                  :step="1"
                  :marks="costMarks"
                  @change="initSimChart"
                />
              </div>
              <div class="yearly-sim-control">
                <div class="yearly-sim-label-row">
                  <div class="yearly-sim-label">保本次数</div>
                  <div class="yearly-sim-breakeven">
                    {{ simValid ? simBreakevenUsed : '—' }}<span class="yearly-sim-unit"> 次</span>
                  </div>
                </div>
              </div>
              <div class="yearly-sim-results">
                <div class="yearly-sim-result">
                  <div class="yearly-sim-value pay-color">{{ simValid ? simLossCount : '—' }}</div>
                  <div class="yearly-sim-name">预计用超卡（张）</div>
                </div>
                <div class="yearly-sim-result">
                  <div class="yearly-sim-value pay-color">{{ simValid ? `${simLossRatio}%` : '—' }}</div>
                  <div class="yearly-sim-name">预计用超占比</div>
                </div>
                <div class="yearly-sim-result">
                  <div class="yearly-sim-value" :class="simTotalProfitClass">
                    <template v-if="simValid">{{ simTotalProfit | toYuan }}</template>
                    <template v-else>—</template>
                  </div>
                  <div class="yearly-sim-name">预计价值差额（元）</div>
                </div>
              </div>
            </div>
            <div id="yearly-sim-chart" class="yearly-sim-chart"></div>
          </div>
        </div>
      </section>
    </template>

    <el-table-column width="70px" label="序号">
      <template v-slot="{ $index }">{{ $index + 1 }}</template>
    </el-table-column>

    <v-column-student label="学员" link-page-id="student_id"/>

    <el-table-column label="手机号" prop="phone" width="130px"/>

    <el-table-column label="办卡门店" prop="store_name" width="110px"/>

    <el-table-column label="套餐名称" prop="card_charge_type_name" width="180px"/>

    <el-table-column label="过期日期" prop="expire_time" width="110px"/>

    <el-table-column label="实付金额（元）" prop="actual_payment" width="120px">
      <template v-slot="{ row }">{{ row.actual_payment | toYuan }}</template>
    </el-table-column>

    <el-table-column
      label="使用次数 / 保本线（次）"
      prop="used_number"
      sortable="custom"
      :sort-orders="['descending', 'ascending']"
      width="210px"
    >
      <template v-slot="{ row }">
        <el-tooltip content="保本线按实付金额 ÷ 75 元/次折算，使用次数超过保本线即用超票面" placement="top">
          <span :class="row.profit >= 0 ? 'income-color' : 'pay-color'">
            {{ row.used_number }} / {{ row.breakeven_used }}
          </span>
        </el-tooltip>
      </template>
    </el-table-column>

    <el-table-column
      label="价值差额（元）"
      prop="profit"
      sortable="custom"
      :sort-orders="['descending', 'ascending']"
      width="150px"
    >
      <template v-slot="{ row }">
        <span :class="row.profit >= 0 ? 'income-color' : 'pay-color'">{{ row.profit | toYuan }}</span>
      </template>
    </el-table-column>

    <el-table-column label="价值状态" width="90px">
      <template v-slot="{ row }">
        <el-tag
          v-for="state in findYearlyCardProfitState(toYuan(row.profit))"
          :key="state.value"
          :type="state.tagType"
          size="small"
        >
          {{ state.name }}
        </el-tag>
      </template>
    </el-table-column>

    <template slot="mobile-list">
      <section class="list">
        <template v-if="list.length">
          <div
            v-for="(v, k) in list"
            :key="`yearly-card-${k}-${v.card_id}`"
            class="list-item primary-background-light"
          >
            <div class="list-item-detail">
              <span>学员</span>
              <span>{{ v.student_name }}</span>
            </div>
            <div class="list-item-detail">
              <span>手机号</span>
              <span>{{ v.phone }}</span>
            </div>
            <div class="list-item-detail">
              <span>办卡门店</span>
              <span>{{ v.store_name }}</span>
            </div>
            <div class="list-item-detail">
              <span>套餐名称</span>
              <span>{{ v.card_charge_type_name }}</span>
            </div>
            <div class="list-item-detail">
              <span>过期日期</span>
              <span>{{ v.expire_time }}</span>
            </div>
            <div class="list-item-detail">
              <span>实付金额</span>
              <span>{{ v.actual_payment | toYuan }} 元</span>
            </div>
            <div class="list-item-detail">
              <span>使用次数 / 保本线</span>
              <span :class="v.profit >= 0 ? 'income-color' : 'pay-color'">
                {{ v.used_number }} / {{ v.breakeven_used }} 次
              </span>
            </div>
            <div class="list-item-detail">
              <span>价值差额</span>
              <span :class="v.profit >= 0 ? 'income-color' : 'pay-color'">
                {{ v.profit | toYuan }} 元
              </span>
            </div>
          </div>
        </template>
        <v-empty :is-loading="isLoading" :length="list.length"/>
      </section>
    </template>
  </v-table>
</template>

<script>
import RouteConfig from '@/router/config';
import { getLegalQueryList } from '@/utils/formats';
import { getUsedNumberOfYearlyCard } from '@/api/card';
import { toYuan } from '@/utils/number';
import { yearlyCardProfitStates, findYearlyCardProfitState } from '@/enums/yearlyCardProfitStates';
import { yearlyCardUsedScopes } from '@/enums/yearlyCardUsedScopes';
import { yearlyCardGenericScopes } from '@/enums/yearlyCardGenericScopes';
import exportExcel from '@/utils/excel-export';
import * as echarts from 'echarts/lib/echarts';
import 'echarts/lib/chart/bar';
import 'echarts/lib/chart/line';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/grid';
import 'echarts/lib/component/markPoint';

export default {
  name: RouteConfig.cardChargeTypeYearlyAnalysis.name,
  data: () => ({
    RouteConfig,
    yearlyCardProfitStates,
    yearlyCardUsedScopes,
    yearlyCardGenericScopes,
    isLoading: false,
    isExporting: false,
    currentExportPage: 0,
    count: 0,
    list: [],
    summary: {
      total_cards: 0,
      total_payment: 0,
      avg_payment: 0,
      total_used: 0,
      avg_used: 0,
      total_profit: 0,
      profit_count: 0,
      profit_amount: 0,
      loss_count: 0,
      loss_amount: 0,
      breakeven_count: 0,
      light_used_count: 0,
      light_used_profit: 0,
      heavy_used_count: 0,
      heavy_used_profit: 0,
      avg_profit: 0,
    },
    distribution: [],
    profitDistribution: [],
    usedNumbers: [],
    avgBreakevenUsed: 0,
    costPerClass: 75,
    // 活动定价模拟器参数（默认活动价取平均实付金额，首次加载后覆盖；此前用 14988 兜底）
    isSimInited: false,
    simPrice: 14988,
    simCost: 75,
    priceMarks: {
      14988: '14988',
      17988: '17988',
    },
    costMarks: {
      50: '50',
      60: '60',
      75: '75',
    },
    profitChart: null,
    usedChart: null,
    simChart: null,
    queryList: {
      store_id: '',
      keyword: '',
      start_date: '',
      end_date: '',
      profit_state: '',
      used_scope: '',
      is_generic: 1,
      order_by: '',
      is_asc: 0,
      page: 1,
      size: 10,
    },
  }),
  computed: {
    exportAllText() {
      return `导出全部至 Excel${this.isExporting ? `（${Math.min(this.currentExportPage * 30, this.count)} / ${this.count}）` : ''}`;
    },
    // 当前页导出数据
    xlsData() {
      return this.list.map((item) => ({
        '学员姓名': item.student_name,
        '手机号': item.phone,
        '办卡门店': item.store_name,
        '套餐名称': item.card_charge_type_name,
        '过期日期': item.expire_time,
        '实付金额（元）': toYuan(item.actual_payment),
        '保本线（次）': item.breakeven_used,
        '使用次数（次）': item.used_number,
        '价值差额（元）': toYuan(item.profit),
        '价值状态': findYearlyCardProfitState(toYuan(item.profit))[0].name,
      }));
    },
    // 总价值差额颜色：未用超红（income-color）、用超绿（pay-color），与系统收支颜色惯例一致
    totalProfitClass() {
      return this.summary.total_profit >= 0 ? 'income-color' : 'pay-color';
    },
    // 整体价值利用率（%）：学员整体消耗的次卡等值 ÷ 实付金额，低于 100% 说明活动有安全空间
    valueUsageRate() {
      if (!this.summary.total_payment) return 0;
      return Math.round(this.summary.total_used * 7500 / this.summary.total_payment * 1000) / 10;
    },
    // 模拟器：活动价与单次成本均为正数时才有效推演
    simValid() {
      return +this.simPrice > 0 && +this.simCost > 0;
    },
    // 模拟器：保本次数 = 活动价 ÷ 单次成本（向上取整）
    simBreakevenUsed() {
      return this.simValid ? Math.ceil(+this.simPrice / +this.simCost) : 0;
    },
    // 模拟器：使用次数 ≥ 保本次数即用超票面
    simLossCount() {
      if (!this.simValid) return 0;
      const breakeven = this.simBreakevenUsed;
      return this.usedNumbers.reduce((total, used) => total + (used >= breakeven ? 1 : 0), 0);
    },
    simLossRatio() {
      if (!this.simValid || !this.usedNumbers.length) return 0;
      return Math.round(this.simLossCount * 1000 / this.usedNumbers.length) / 10;
    },
    // 模拟器：所有卡统一按活动价收款时的预计价值差额（单位：分）
    simTotalProfit() {
      if (!this.simValid) return 0;
      const priceCents = Math.round(+this.simPrice * 100);
      const costCents = Math.round(+this.simCost * 100);
      return this.usedNumbers.reduce((total, used) => total + (priceCents - used * costCents), 0);
    },
    simTotalProfitClass() {
      if (!this.simValid) return '';
      return this.simTotalProfit >= 0 ? 'income-color' : 'pay-color';
    },
  },
  watch: {
    '$route'(to) {
      this.load(to);
    },
  },
  beforeMount() {
    this.load();
  },
  beforeDestroy() {
    if (this.profitChart) {
      this.profitChart.dispose();
      this.profitChart = null;
    }
    if (this.usedChart) {
      this.usedChart.dispose();
      this.usedChart = null;
    }
    if (this.simChart) {
      this.simChart.dispose();
      this.simChart = null;
    }
  },
  methods: {
    findYearlyCardProfitState,
    toYuan,
    async load(route = this.$route, next) {
      const { queryList } = this;
      getLegalQueryList(queryList, route.query);

      this.isLoading = true;
      const { isError, data } = await getUsedNumberOfYearlyCard(queryList);
      this.isLoading = false;
      if (isError) return;
      if (next) next();

      this.count = data.count;
      this.list = data.list;
      this.summary = data.summary;
      this.distribution = data.distribution;
      this.profitDistribution = data.profit_distribution;
      this.usedNumbers = data.used_numbers || [];
      this.avgBreakevenUsed = data.avg_breakeven_used || 0;
      this.costPerClass = data.cost_per_class || 75;

      // 模拟器首次加载时初始化：单次成本取后端默认值，活动价取平均实付金额，之后保留用户调整
      if (!this.isSimInited) {
        this.simCost = this.costPerClass;
        this.simPrice = toYuan(this.summary.avg_payment) || 14988;
        this.isSimInited = true;
      }

      this.$nextTick(() => {
        this.initProfitChart();
        this.initUsedChart();
        this.initSimChart();
      });
    },
    // 筛选下拉变更（价值状态 / 使用程度 / 通用性）：清空时保持空字符串，避免回显数字 0，占位提示正常显示
    handleFilterSelectChange(key) {
      // 通用性下拉不可清空，0 是合法选项「全部课卡」，不做空值归一化
      if (key !== 'is_generic') {
        this.queryList[key] = this.queryList[key] || '';
      }
      this.queryList.page = 1;
      RouteConfig.cardChargeTypeYearlyAnalysis.push(this.queryList);
    },
    // 点击价值状态/使用程度卡片：切换对应筛选并刷新列表，再次点击取消筛选
    // filterKey 为 'profit_state'（价值状态）或 'used_scope'（使用程度）
    handleSummaryCardClick(filterKey, value) {
      this.queryList[filterKey] = this.queryList[filterKey] === value ? '' : value;
      this.queryList.page = 1;
      RouteConfig.cardChargeTypeYearlyAnalysis.push(this.queryList);
    },
    // 价值差额分布直方图
    initProfitChart() {
      const chartBox = document.getElementById('yearly-profit-chart');
      if (!chartBox) return;

      if (!this.profitChart) {
        this.profitChart = echarts.init(chartBox);
      }

      const items = this.profitDistribution;
      const labels = items.map((item) => item.label);
      const counts = items.map((item) => item.count);
      const profits = items.map((item) => item.profit);
      // 重亏→白赚：绿（亏/支出）→灰（临界）→红（赚/收入），与系统收支颜色惯例一致
      const colors = ['#2E7D32', '#67C23A', '#B3E19D', '#909399', '#F79A9A', '#F56C6C', '#C45656'];

      this.profitChart.setOption({
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          formatter(params) {
            const index = params[0].dataIndex;
            return `${labels[index]}<br/>年卡张数：${counts[index]} 张<br/>合计价值差额：${toYuan(profits[index])} 元`;
          },
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: labels,
          axisLabel: {
            interval: 0,
          },
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
        },
        series: [
          {
            name: '年卡张数',
            type: 'bar',
            barMaxWidth: 48,
            data: counts,
            itemStyle: {
              color(params) {
                return colors[params.dataIndex];
              },
            },
          },
        ],
      }, true);
    },
    // 使用次数分布柱状图
    initUsedChart() {
      const chartBox = document.getElementById('yearly-used-chart');
      if (!chartBox) return;

      if (!this.usedChart) {
        this.usedChart = echarts.init(chartBox);
      }

      const labels = this.distribution.map((item) => item.label);
      const counts = this.distribution.map((item) => item.count);
      // 闲置区间（≤5 次，学员几乎未使用）标红、高频区间（≥200 次，学员用超票面）标绿，其余蓝色
      const colors = this.distribution.map((item) => {
        const label = item.label;
        if (label === '0次' || label === '1~5次') return '#F56C6C';
        if (label === '200次以上') return '#67C23A';
        return '#409EFF';
      });

      this.usedChart.setOption({
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: labels,
          axisLabel: {
            interval: 0,
          },
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
        },
        series: [
          {
            name: '年卡张数',
            type: 'bar',
            barMaxWidth: 48,
            data: counts,
            itemStyle: {
              color(params) {
                return colors[params.dataIndex];
              },
            },
          },
        ],
      }, true);
    },
    // 定价模拟器：活动价与预计用超占比曲线
    initSimChart() {
      const chartBox = document.getElementById('yearly-sim-chart');
      if (!chartBox) return;

      if (!this.simChart) {
        this.simChart = echarts.init(chartBox);
      }

      // 活动价或单次成本未填写时清空曲线
      if (!this.simValid) {
        this.simChart.clear();
        return;
      }

      // 按 500 元一档推演各活动价下的用超占比，区间随当前输入值自适应（默认至少覆盖 1~2 万）
      const minPrice = Math.min(10000, Math.floor(+this.simPrice / 500) * 500);
      const maxPrice = Math.max(20000, Math.ceil(+this.simPrice / 500) * 500);
      const prices = [];
      const ratios = [];
      for (let price = minPrice; price <= maxPrice; price += 500) {
        prices.push(price);
        const breakeven = Math.ceil(price / +this.simCost);
        const lossCount = this.usedNumbers.reduce((total, used) => total + (used >= breakeven ? 1 : 0), 0);
        ratios.push(this.usedNumbers.length ? +(lossCount * 100 / this.usedNumbers.length).toFixed(1) : 0);
      }

      // 定位当前输入的活动价在曲线上的最近点位
      let nearestIndex = 0;
      let minDiff = Infinity;
      prices.forEach((price, index) => {
        const diff = Math.abs(price - +this.simPrice);
        if (diff < minDiff) {
          minDiff = diff;
          nearestIndex = index;
        }
      });

      this.simChart.setOption({
        tooltip: {
          trigger: 'axis',
          formatter(params) {
            const index = params[0].dataIndex;
            return `活动价：${prices[index]} 元<br/>预计用超占比：${ratios[index]}%`;
          },
        },
        grid: {
          left: '3%',
          right: '6%',
          bottom: '3%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: prices,
          axisLabel: {
            interval: 9,
          },
          name: '活动价（元）',
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: '{value}%',
          },
        },
        series: [
          {
            name: '用超占比',
            type: 'line',
            data: ratios,
            smooth: true,
            symbol: 'none',
            lineStyle: {
              color: '#67C23A',
            },
            itemStyle: {
              color: '#67C23A',
            },
            markPoint: {
              symbolSize: 52,
              label: {
                formatter(params) {
                  // echarts 4 中仅传 coord 时 {c} 取不到值，需显式带上 value 并从此处读取
                  const data = params.data || {};
                  const ratio = data.value !== undefined ? data.value : (data.coord ? data.coord[1] : 0);
                  return `${ratio}%`;
                },
              },
              data: [
                {
                  coord: [nearestIndex, ratios[nearestIndex]],
                  value: ratios[nearestIndex],
                },
              ],
            },
          },
        ],
      }, true);
    },
    async exportAllExcel() {
      if (this.isExporting) return;
      if (!this.count) return this.$message({
        message: '暂无数据可以导出',
        type: 'info',
        duration: 5000,
      });

      const isConfirm = await this.$confirm(
        `此操作将导出共计 ${this.count} 条数据，导出过程中不可关闭此页面，确定是否要继续？`,
        '提示',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        },
      ).catch(() => {});
      if (!isConfirm) return;

      this.isExporting = true;
      try {
        // 拷贝当前筛选条件，逐页翻查时不污染页面状态
        const params = { ...this.queryList };
        getLegalQueryList(params, this.$route.query);

        Object.assign(params, {
          page: 1,
          size: 30,
        });

        let xlsData = [];
        const maxPage = Math.ceil(this.count / 30);
        for (let page = 1; page <= maxPage; page++) {
          params.page = page;
          this.currentExportPage = page;
          const { isError, data } = await getUsedNumberOfYearlyCard(params);
          if (isError || !data) {
            this.$message({
              message: '导出失败，请重试',
              type: 'error',
              duration: 5000,
            });
            return;
          }
          xlsData = xlsData.concat(data.list);
        }

        exportExcel(xlsData.map((item) => ({
          '学员姓名': item.student_name,
          '手机号': item.phone,
          '办卡门店': item.store_name,
          '套餐名称': item.card_charge_type_name,
          '过期日期': item.expire_time,
          '实付金额（元）': toYuan(item.actual_payment),
          '保本线（次）': item.breakeven_used,
          '使用次数（次）': item.used_number,
          '价值差额（元）': toYuan(item.profit),
          '价值状态': findYearlyCardProfitState(toYuan(item.profit))[0].name,
        })), '年卡使用分析');
      } finally {
        this.isExporting = false;
      }
    },
  },
};
</script>

<style src="./yearly-analysis.css"></style>
